import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { Pencil, Trash2, Check, X } from "lucide-react";

/**
 * Edit + Delete buttons + inline edit form for a chat message.
 * Always visible (not hover-gated) so they work on touch devices.
 *
 * Delete is soft + Undo:
 *   1. Optimistically remove the message from the parent list (onDeleted)
 *   2. Backend marks Deleted_At — list endpoint hides it
 *   3. Toast shows an "Undo" button for ~5s
 *      → click Undo → POST /restore → onRestored(msg) puts it back
 *
 * Visible when the current user is the sender OR an admin.
 * System messages are non-editable / non-deletable.
 *
 * Props:
 *   message    — the message doc (must have id, Channel_ID, Sender_ID, Body)
 *   user       — current user
 *   onUpdated(updated)  — called with the fresh message after edit
 *   onDeleted(id)       — called after delete (optimistic — remove from list)
 *   onRestored(message) — called after Undo (re-insert into list, sorted)
 *   compact    — bool, renders smaller buttons (for the order-chat drawer)
 */
export default function MessageActions({ message, user, onUpdated, onDeleted, onRestored, compact = false }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.Body || "");
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  if (message.Sender_ID === "system") return null;
  const canMutate = message.Sender_ID === user.id || user.role === "admin";
  if (!canMutate) return null;

  const save = async () => {
    const next = draft.trim();
    if (!next) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/channels/${message.Channel_ID}/messages/${message.id}`,
        { Body: next }
      );
      onUpdated?.(data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async () => {
    // Optimistic: drop from the parent list immediately so the UI feels instant.
    const snapshot = { ...message };
    onDeleted?.(message.id);
    try {
      await api.delete(`/channels/${message.Channel_ID}/messages/${message.id}`);
      // Offer a 5-second Undo
      toast.info("Message deleted.", {
        duration: 5000,
        action: {
          label: "Undo",
          testid: `msg-undo-${message.id}`,
          onClick: async () => {
            try {
              const { data } = await api.post(
                `/channels/${snapshot.Channel_ID}/messages/${snapshot.id}/restore`,
              );
              onRestored?.(data || snapshot);
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            }
          },
        },
      });
    } catch (e) {
      // Server rejected — restore the optimistic deletion
      onRestored?.(snapshot);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (editing) {
    return (
      <div className="mt-1 flex items-start gap-2">
        <textarea
          autoFocus
          rows={Math.min(6, Math.max(2, draft.split("\n").length))}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setEditing(false); setDraft(message.Body || ""); }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
          }}
          className="es-input flex-1 text-sm"
          data-testid={`msg-edit-input-${message.id}`}
        />
        <div className="flex flex-col gap-1">
          <button
            data-testid={`msg-edit-save-${message.id}`}
            onClick={save} disabled={saving}
            className="p-1.5 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
            title="Save (⌘/Ctrl + Enter)"
          ><Check className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => { setEditing(false); setDraft(message.Body || ""); }}
            className="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100"
            title="Cancel (Esc)"
          ><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    );
  }

  const sz = compact ? "p-1" : "p-1.5";
  const ic = compact ? "w-3 h-3" : "w-3.5 h-3.5";
  return (
    <div className="inline-flex items-center gap-0.5 ml-1 opacity-50 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button
        data-testid={`msg-edit-${message.id}`}
        onClick={() => setEditing(true)}
        className={`${sz} rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:text-zinc-900`}
        title="Edit message"
        aria-label="Edit message"
      ><Pencil className={ic} /></button>
      <button
        data-testid={`msg-delete-${message.id}`}
        onClick={remove}
        className={`${sz} rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 focus:text-red-600`}
        title="Delete message"
        aria-label="Delete message"
      ><Trash2 className={ic} /></button>
    </div>
  );
}

export function EditedTag({ message }) {
  if (!message.Edited_At) return null;
  const ts = new Date(message.Edited_At);
  const title = `Edited ${ts.toLocaleString()}`;
  return (
    <span className="text-[10px] text-zinc-400 ml-1 italic" title={title}>(edited)</span>
  );
}
